import { createClient } from "@supabase/supabase-js";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

export const supabase = createClient(supabaseUrl, supabaseKey);

// Cached access token used to pause the exam timer reliably on tab close/hide,
// since a regular fetch can be aborted by the browser before it completes on unload.
let cachedAccessToken = null;

supabase.auth.getSession().then(({ data }) => {
  cachedAccessToken = data?.session?.access_token || null;
});

supabase.auth.onAuthStateChange((_event, session) => {
  cachedAccessToken = session?.access_token || null;
});

export function pauseExamTimerOnUnload(examId) {
  if (!examId || !cachedAccessToken) return;

  try {
    fetch(`${supabaseUrl}/rest/v1/rpc/pause_exam_timer`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        apikey: supabaseKey,
        Authorization: `Bearer ${cachedAccessToken}`,
      },
      body: JSON.stringify({ p_exam_id: examId }),
    }).catch(() => {});
  } catch (error) {
    console.error('Error pausing timer on unload:', error);
  }
}

export async function getDashboardSummaryCards() {
  const { data, error } = await supabase.rpc("get_dashboard_summary_cards");
  if (error) {
    console.error("Error obteniendo estadísticas:", error);
    throw error;
  }

  return data;
}


export const getRandomQuestions = async (materiasSeleccionadas, numPreguntas, dificultad, includeAnswers) => {
    const { data, error } = await supabase.rpc(
        'get_random_exam_items_grouped',
        {
            p_specialty_ids: materiasSeleccionadas,
            p_total_questions: numPreguntas,
            p_case_percentage: 0.4,
            p_include_answers: includeAnswers,
            p_difficulty_filter: dificultad || null,
        }
    );
    return { data, error };
}

export const setQuestionsByBlock = (questions) => {
    const blocksMap = new Map();

      for (const item of questions) {
        const blockId = item.block_id;

        if (!blocksMap.has(blockId)) {
          blocksMap.set(blockId, {
            blockId,
            itemType: item.item_type,
            clinicalCase: item.clinical_case,
            questions: [],
          });
        }

        blocksMap.get(blockId).questions.push(item);
      }

      const preguntasArray = Array.from(blocksMap.values());
      return preguntasArray;
}

export async function createExam({
  selectedSpecialtyIds,
  totalQuestions = 20,
  casePercentage = 0.4,
  difficultyFilter = 'false',
  durationMinutes = 60,
  modo = 'aleatorio',
  studyPlanTaskId = null,
}) {
  if (!selectedSpecialtyIds || selectedSpecialtyIds.length === 0) {
    throw new Error('Debes seleccionar al menos una especialidad.');
  }

  // 1. Obtener usuario actual
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError) {
    throw userError;
  }

  if (!user) {
    throw new Error('Usuario no autenticado.');
  }

  // 2. Traer preguntas/casos clínicos random
  const { data: examItems, error: itemsError } = await supabase.rpc(
    'get_random_exam_items_grouped',
    {
      p_specialty_ids: selectedSpecialtyIds,
      p_total_questions: totalQuestions,
      p_case_percentage: casePercentage,
      p_include_answers: false,
      p_difficulty_filter: difficultyFilter,
    }
  );

  if (itemsError) {
    throw itemsError;
  }

  if (!examItems || examItems.length === 0) {
    throw new Error('No se encontraron preguntas para los filtros seleccionados.');
  }

  // 3. Crear examen
  const { data: exam, error: examError } = await supabase
    .from('exams')
    .insert({
      user_id: user.id,
      title: 'Simulador ' + modo,
      description: 'Examen generado en modo ' + modo,
      status: 'draft',
      number_of_questions: examItems.length,
      duration_minutes: durationMinutes,

      // Si solo seleccionó una especialidad, la guardamos directo.
      // Si seleccionó varias, usamos settings.
      specialty_id:
        selectedSpecialtyIds.length === 1 ? selectedSpecialtyIds[0] : null,

      difficulty:
        difficultyFilter && difficultyFilter !== 'false'
          ? difficultyFilter
          : null,

      settings: {
        selected_specialty_ids: selectedSpecialtyIds,
        requested_total_questions: totalQuestions,
        actual_total_questions: examItems.length,
        case_percentage: casePercentage,
        difficulty_filter: difficultyFilter,
        study_plan_task_id: studyPlanTaskId || null,
      },
    })
    .select()
    .single();

  if (examError) {
    throw examError;
  }

  // 4. Guardar preguntas del examen
  const examQuestionsPayload = examItems.map((item, index) => ({
    exam_id: exam.id,
    question_id: item.question_id,
    position: index + 1,
  }));

  const { error: examQuestionsError } = await supabase
    .from('exam_questions')
    .insert(examQuestionsPayload);

  if (examQuestionsError) {
    throw examQuestionsError;
  }

  return {
    exam,
    items: examItems,
  };
}

export async function getExamById(examId) {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError) throw userError;
  if (!user) throw new Error('Usuario no autenticado.');

  const { data: exam, error: examError } = await supabase
    .from('exams')
    .select('*')
    .eq('id', examId)
    .single();

  if (examError) throw examError;

  const { data: items, error: itemsError } = await supabase.rpc(
    'get_exam_items_by_exam_id',
    {
      p_exam_id: examId,
      p_include_answers: false,
    }
  );

  if (itemsError) throw itemsError;

  const { data: answers, error: answersError } = await supabase
    .from('user_answers')
    .select('question_id, selected_option_id, answered_at, time_spent_seconds')
    .eq('exam_id', examId)
    .eq('user_id', user.id);

  if (answersError) throw answersError;

  return {
    exam,
    items,
    answers,
  };
}

export function groupExamItems(items) {
  const blocksMap = new Map();

  for (const item of items) {
    const blockId = item.block_id;

    if (!blocksMap.has(blockId)) {
      blocksMap.set(blockId, {
        blockId,
        itemType: item.item_type,
        clinicalCase: item.clinical_case,
        specialtyName: item.specialty_name,
        questions: [],
      });
    }

    blocksMap.get(blockId).questions.push(item);
  }

  return Array.from(blocksMap.values());
}

export async function saveExamAnswer({
  examId,
  questionId,
  selectedOptionId,
  timeSpentSeconds = null,
}) {
  const { data, error } = await supabase.rpc('submit_exam_answer', {
    p_exam_id: examId,
    p_question_id: questionId,
    p_selected_option_id: selectedOptionId,
    p_time_spent_seconds: timeSpentSeconds,
  });

  if (error) {
    console.error('Error saving answer:', error);
    throw error;
  }

  return data?.[0];
}

export async function saveExamCurrentPosition(examId, currentPosition) {
  const { error } = await supabase.rpc('update_exam_current_position', {
    p_exam_id: examId,
    p_current_position: currentPosition,
  });

  if (error) {
    console.error('Error saving current position:', error);
    throw error;
  }
}

// El destino del correo se configura en la Edge Function "report-question", no aqui.
export async function reportQuestion({
  examId,
  questionId,
  clinicalCase,
  questionText,
  options,
  selectedOptionText,
  userComment,
}) {
  const { data, error } = await supabase.functions.invoke('report-question', {
    body: {
      exam_id: examId,
      question_id: questionId,
      clinical_case: clinicalCase || null,
      question_text: questionText,
      options,
      selected_option_text: selectedOptionText || null,
      user_comment: userComment,
    },
  });

  if (error) {
    console.error('Error reporting question:', error);
    throw error;
  }

  return data;
}

export async function startExamTimer(examId) {
  const { data, error } = await supabase.rpc('start_exam_timer', {
    p_exam_id: examId,
  });

  if (error) {
    console.error('Error starting exam timer:', error);
    throw error;
  }

  return data?.[0];
}

export async function finishExam(examId) {
  const { data, error } = await supabase.rpc('finish_exam', {
    p_exam_id: examId,
  });

  if (error) {
    console.error('Error finishing exam:', error);
    throw error;
  }

  return data?.[0];
}

export function formatTime(seconds) {
  const safeSeconds = Math.max(0, seconds || 0);

  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const secs = safeSeconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

export async function resumeExamTimer(examId) {
  const { data, error } = await supabase.rpc('resume_exam_timer', {
    p_exam_id: examId,
  });

  if (error) throw error;

  return data?.[0];
}

export async function pauseExamTimer(examId) {
  const { data, error } = await supabase.rpc('pause_exam_timer', {
    p_exam_id: examId,
  });

  if (error) throw error;

  return data?.[0];
}

export async function getExamTimerState(examId) {
  const { data, error } = await supabase.rpc('get_exam_timer_state', {
    p_exam_id: examId,
  });

  if (error) throw error;

  return data?.[0];
}

export async function getExamDashboard(limit = 5, timezone = 'America/Mexico_City') {
  const { data, error } = await supabase.rpc('get_user_exam_dashboard', {
    p_limit: limit,
    p_timezone: timezone,
  });

  return { data, error };
}

export async function getExamResultsPayload(examId) {
  const { data, error } = await supabase.rpc("get_exam_results_payload", {
    p_exam_id: examId,
  });

  return { data, error };
}

export async function getReviewPageData(examId) {
  const { data, error } = await supabase.rpc("get_exam_review_page_data", {
    p_exam_id: examId,
  });

  return { data, error };
}

export async function getExamReviewQuestionDetail(examId, questionId) {
  const { data, error } = await supabase.rpc("get_exam_review_question_detail", {
    p_exam_id: examId,
    p_question_id: questionId,
  });

  return { data, error };
}

export async function getQuestionsByFilter(examId, filter = 'all') {
  const { data, error } = await supabase.rpc("get_exam_review_questions_by_filter", {
    p_exam_id: examId,
    p_filter: filter, // all | correct | incorrect | unanswered
  });

  return { data, error };
}

export async function getAdjacentQuestion(examId, currentQuestionId, direction = 'next') {
  const { data, error } = await supabase.rpc("get_exam_review_adjacent_question", {
    p_exam_id: examId,
    p_question_id: currentQuestionId,
    p_direction: direction,
  });

  return { data, error };
}
export async function getPracticePageData(practiceGoal = 20) {
  const { data, error } = await supabase.rpc("get_practice_page_data", {
    p_daily_goal: practiceGoal,
  });

  return { data, error };
}

export async function createPracticeSession(specialtyIds, totalQuestions = 0, difficulty, includeImages = false, onlyClinicalCases = false, onlyIncorrect = false, clinicalCasePercentage = 40) {
  const normalizedSpecialtyIds = Array.isArray(specialtyIds) && specialtyIds.length > 0
    ? specialtyIds
    : [];

  const normalizedTotalQuestions = Number.isFinite(Number(totalQuestions))
    ? Math.max(0, Math.trunc(Number(totalQuestions)))
    : 0;

  const normalizedDifficultyRaw =
    typeof difficulty === "string" ? difficulty.trim().toLowerCase() : difficulty;
  const normalizedDifficulty =
    !normalizedDifficultyRaw ||
    normalizedDifficultyRaw === "false" ||
    normalizedDifficultyRaw === "all" ||
    normalizedDifficultyRaw === "any"
      ? null
      : normalizedDifficultyRaw;

  const { data, error } = await supabase.rpc("create_practice_session", {
    p_specialty_ids: normalizedSpecialtyIds,
    p_total_questions: normalizedTotalQuestions,
    p_difficulty: normalizedDifficulty,
    p_include_images: includeImages,
    p_include_clinical_cases: !onlyClinicalCases,
    p_only_clinical_cases: onlyClinicalCases,
    p_only_incorrect: onlyIncorrect,
    p_clinical_case_percentage: 0,
  });

  return { data, error };
}

export async function getPracticeSessionItems(sessionId) {
  const { data, error } = await supabase.rpc("get_practice_session_items", {
    p_study_session_id: sessionId,
  });

  return { data, error };
}

export async function savePracticeAnswer(sessionId, questionId, optionId, timeSpentSeconds = 42) {
  const { data, error } = await supabase.rpc("save_practice_answer", {
    p_study_session_id: sessionId,
    p_question_id: questionId,
    p_selected_option_id: optionId,
    p_time_spent_seconds: timeSpentSeconds,
  });

  return { data, error };
}

export async function finishPracticeSession(sessionId) {
  const { data, error } = await supabase.rpc("finish_practice_session", {
    p_study_session_id: sessionId,
  });

  return { data, error };
}

export async function getPracticeResults(sessionId) {
  const { data, error } = await supabase.rpc("get_practice_results_payload", {
    p_study_session_id: sessionId,
  });
  return { data, error };
}

export async function getPracticeSessionReviewQuestionByFilter(sessionId, filter = "all") {
  const { data, error } = await supabase.rpc("get_practice_review_questions_by_filter", {
    p_study_session_id: sessionId,
    p_filter: filter,
  });
  return { data, error };
}

export async function getPracticeSessionReviewQuestionDetail(sessionId, questionId) {
  const { data, error } = await supabase.rpc("get_practice_review_question_detail", {
    p_study_session_id: sessionId,
    p_question_id: questionId,
  });
  return { data, error };
}

export async function getDashboardStats() {
  const timezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    "America/Mexico_City";

  const { data, error } = await supabase.rpc(
    "get_user_dashboard_stats",
    {
      p_timezone: timezone,
    }
  );

  if (error) {
    console.error("Error obteniendo estadísticas:", error);
    throw error;
  }

  return data;
}

export async function getSectionPerformance() {
  const { data, error } = await supabase.rpc(
    "get_user_section_performance",
    {
      p_limit: 4,
      p_sections: null,
      p_include_empty: false,
    }
  );

  if (error) {
    console.error(
      "Error obteniendo desempeño por sección:",
      error
    );

    throw error;
  }

  return data;
}

export async function getWeeklyExamProgress({
  limit = 5,
  onlyCurrentWeek = false,
} = {}) {
  const timezone =
    "America/Mexico_City";

  const { data, error } = await supabase.rpc(
    "get_weekly_exam_progress",
    {
      p_limit: limit,
      p_only_current_week: onlyCurrentWeek,
      p_timezone: timezone,
    }
  );

  if (error) {
    console.error(
      "Error obteniendo progreso semanal:",
      error
    );

    throw error;
  }

  return data;
}

export async function createFlashcardSession(selectedSpecialtyIds, totalCards, mode) {
  const { data, error } = await supabase.rpc("create_flashcard_session", {
    p_specialty_ids: selectedSpecialtyIds && selectedSpecialtyIds.length > 0 ? selectedSpecialtyIds : [],
    p_total_cards: totalCards || 0,
    p_mode: mode || 'due',
  });
  return { data, error };
}

export async function getNextFlashcard(sessionId) {
  const { data, error } = await supabase.rpc("get_next_flashcard", {
    p_flashcard_session_id: sessionId,
  });
  return { data, error };
}

export async function saveFlashcardReview(flashcardSessionId, currentCard, rating, responseTimeSeconds) {
  const { data, error } = await supabase.rpc("save_flashcard_review", {
  p_flashcard_session_id: flashcardSessionId,
  p_flashcard_id: currentCard.flashcard.id,
  p_rating: rating,
  p_response_time_seconds: responseTimeSeconds,
  });
  return { data, error };
}

export async function suspendFlashcard(currentCard) {
  const { data, error } = await supabase.rpc("suspend_flashcard", {
    p_flashcard_id: currentCard.flashcard.id,
  });
  return { data, error };
}

export async function suspendFlashcardInSession(flashcardSessionId, currentCard) {
  const { data, error } = await supabase.rpc("suspend_flashcard_in_session", {
    p_flashcard_session_id: flashcardSessionId,
    p_flashcard_id: currentCard.flashcard.id,
  });
  return { data, error };
}

export async function finishFlashcardSession(flashcardSessionId) {
  const { data, error } = await supabase.rpc("finish_flashcard_session", {
    p_flashcard_session_id: flashcardSessionId,
  });
  return { data, error };
}

export async function getFlashcardResultsPayload(flashcardSessionId) {
  const { data, error } = await supabase.rpc("get_flashcard_results_payload", {
    p_flashcard_session_id: flashcardSessionId,
  });
  return { data, error };
}

export async function getRecentActivity() {
  const { data, error } = await supabase.rpc("get_recent_activity", {
    p_limit: 5,
    p_include_active: true,
  });
  return { data, error };
}

export async function getStatisticsDashboard(days = 30) {
  const { data, error } = await supabase.rpc("get_statistics_dashboard", {
    p_days: days,
  });
  return { data, error };
}

export async function getRecentActivityPageData(days = 7, limit = 20) {
  const { data, error } = await supabase.rpc("get_activity_page_data", {
    p_days: days,
    p_limit: limit,
    p_type: "all",
    p_status: "all",
    p_search: null,
  });
  return { data, error };
}

export async function getSpecialtyPerformancePageData(days = 30, includeFlashcards = true) {
  const { data, error } = await supabase.rpc(
    "get_specialty_performance_page_data",
    {
      p_days: days,
      p_include_flashcards: includeFlashcards,
    }
  );
  return { data, error };
}

export async function getNextPracticeQuestion(sessionId) {
  const { data, error } = await supabase.rpc("get_next_practice_question", {
    p_study_session_id: sessionId,
  });
  return { data, error };
}

export async function getPracticeSessionResults(sessionId) {
  const { data, error } = await supabase.rpc(
      "get_practice_session_results",
      {
        p_study_session_id: sessionId,
      }
    );
  return { data, error };
}

export async function createStudyPlan(examDate, dailyMinutes, studyDays) {
  const { data, error } = await supabase.rpc("create_study_plan", {
    p_exam_date: examDate,
    p_daily_minutes: dailyMinutes,
    p_study_days: studyDays,
  });
  return { data, error };
}

export async function regenerateStudyPlan(planId) {
  const { data, error } = await supabase.rpc("regenerate_study_plan", {
    p_plan_id: planId,
  });
  return { data, error };
}

export async function startStudyPlanTask(taskId, resultData = {}) {
  const { data, error } = await supabase.rpc("start_study_plan_task", {
    p_task_id: taskId,
    p_result_data: resultData,
  });
  return { data, error };
}

export async function completeStudyPlanTask(taskId, resultData = {}) {
  const { data, error } = await supabase.rpc("complete_study_plan_task", {
    p_task_id: taskId,
    p_result_data: resultData,
  });
  return { data, error };
}

export async function completeStudyPlanTaskSesionPractica(taskId, sessionId, score, correctAnswers, totalQuestions) {
  const { data, error } = await supabase.rpc("complete_study_plan_task", {
    p_task_id: taskId,
    p_result_data: {
      study_session_id: sessionId,
      score: score,
      correct_answers: correctAnswers,
      total_questions: totalQuestions
    }
  });
  return { data, error };
}

export async function completeStudyPlanTaskSimulador(taskId, examId, score) {
  const { data, error } = await supabase.rpc("complete_study_plan_task", {
    p_task_id: taskId,
    p_result_data: {
      exam_id: examId,
      score: score
    }
  });
  return { data, error };
}

export async function completeStudyPlanTaskFlashcards(taskId, flashcardSessionId, reviewedCards, correctCards) {
  const accuracy =
  reviewedCards > 0
    ? Number(((correctCards / reviewedCards) * 100).toFixed(1))
    : 0;

  const { data, error } = await supabase.rpc("complete_study_plan_task", {
    p_task_id: taskId,
    p_result_data: {
      flashcard_session_id: flashcardSessionId,
      reviewed_cards: reviewedCards,
      correct_cards: correctCards,
      accuracy: accuracy
    }
  });
  return { data, error };
}

export async function getMyStudyPlan() {
  const { data, error } = await supabase.rpc(
    "get_my_study_plan"
  );
  return { data, error };
}

export async function updateStudyPlan(planId, examDate, dailyMinutes, studyDays) {
  const { data, error } = await supabase.rpc('update_study_plan', {
    p_plan_id: planId,
    p_exam_date: examDate,
    p_daily_minutes: dailyMinutes,
    p_study_days: studyDays
  });
  return { data, error };
}

export async function createPrivateChallenge(examId, title) {
  const { data, error } = await supabase.rpc(
    "create_private_challenge",
    {
      p_exam_id: examId,
      p_title: title,
      p_expires_at: null,
      p_display_name: null,
    }
  )
  return { data, error };
}

export async function getPrivateChallenge(code) {
  const { data, error } = await supabase.rpc("get_private_challenge", {
    p_invite_code: code,
  });
  return { data, error };
}

export async function joinPrivateChallenge(inviteCode) {
  const { data, error } = await supabase.rpc(
    "join_private_challenge",
    {
      p_invite_code: inviteCode,
    }
  );
  return { data, error };
}

export async function getPrivateChallengeLeaderBoard(inviteCode) {
  const { data, error } = await supabase.rpc(
    "get_private_challenge_leaderboard",
    {
      p_invite_code: inviteCode,
    }
  );
  return { data, error };
}

export async function getMyPrivateChallenges(page = 1, pageSize = 5) {
  const { data, error } = await supabase.rpc(
    "get_my_private_challenges",
    {
      p_page: page,
      p_page_size: pageSize,
    }
  );
  return { data, error };
}